import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, mergeMap, of, switchMap, withLatestFrom} from 'rxjs';
import {CISTERN_STATE_SERVICE_INJECTION_TOKEN, CisternStateService} from '../../../core/services/api/cistern-state-service.interface';
import {CisternActions} from './cistern.actions';
import {Store} from '@ngrx/store';
import {selectCisternFilter} from './cistern.selectors';

@Injectable()
export class CisternEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private cisternStateService = inject<CisternStateService>(CISTERN_STATE_SERVICE_INJECTION_TOKEN);


  public updateCisternFilter$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CisternActions.public.updateCisternFilter),
      mergeMap((_props) => of(CisternActions.private.loadCisternsWithFilters()))
    );
  });

  public loadCisternsWithFilters$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CisternActions.private.loadCisternsWithFilters),
      withLatestFrom(this.store.select(selectCisternFilter)),
      mergeMap(([_action, filter]) => {
        return this.cisternStateService.getCisterns(filter).pipe(
          map((cisternsResponse) => CisternActions.private.loadCisternsSuccess({cisternsResponseDto: cisternsResponse})),
          catchError((error) => of(CisternActions.private.loadCisternsFailure({error})))
        )
      }),
    );
  });

  public loadCisternsWithProvidedFilters$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CisternActions.private.loadCisternsWithProvidedFilters),
      switchMap((props) => this.cisternStateService.getCisterns(
        props.filter,
      ).pipe(
        map((responseDto) => CisternActions.private.loadCisternsWithProvidedFiltersSuccess({
          cisternsResponseDto: responseDto,
        })),
        catchError((error) => of(CisternActions.private.loadCisternsWithProvidedFiltersFailure({
          error: error,
        }))),
      )),
    );
  });

  public loadAllCisternsByFilters$ = createEffect(() => this.actions$.pipe(
      ofType(CisternActions.public.loadAllCisternsByFilters),
      map((props) => CisternActions.private.loadNextOfAll({page: 0, filter: props.filter ?? {}})),
      catchError((error) => of(CisternActions.private.loadAllCisternsByFiltersFailure({
        error: error,
      }))),
    )
  );

  public loadNextOfAll$ = createEffect(() => this.actions$.pipe(
      ofType(CisternActions.private.loadNextOfAll),
      switchMap((props) => {
          const pageSize = 50;
          return this.cisternStateService.getCisterns(
            {...props.filter, pageSize, page: props.page},
          ).pipe(
            map((responseDto) => {
              this.store.dispatch(CisternActions.private.loadNextOfAllSuccess({
                cisternsResponseDto: responseDto,
              }))

              if ((props.page + 1) * pageSize >= responseDto.totalCount) {
                return CisternActions.private.loadAllCisternsByFiltersSuccess()
              }

              return CisternActions.private.loadNextOfAll({page: props.page + 1, filter: props.filter})
            }),
            catchError((error) => of(
              CisternActions.private.loadNextOfAllFailure({error}),
              CisternActions.private.loadAllCisternsByFiltersFailure({error})
            )),
          )
        }
      ),
    )
  );

  public createCistern$ = createEffect(() => this.actions$.pipe(
      ofType(CisternActions.public.createCistern),
      switchMap((props) => this.cisternStateService.createCistern(props.requestDto).pipe(
        map((responseDto) => CisternActions.private.createCisternSuccess({responseDto})),
        catchError((error) => of(CisternActions.private.createCisternFailure({error}))),
      )),
    )
  );

  public updateCistern$ = createEffect(() => this.actions$.pipe(
      ofType(CisternActions.public.updateCistern),
      switchMap((props) => this.cisternStateService.updateCistern(props.id, props.requestDto).pipe(
        map((responseDto) => CisternActions.private.updateCisternSuccess({responseDto})),
        catchError((error) => of(CisternActions.private.updateCisternFailure({id: props.id, error}))),
      )),
    )
  );

  public deleteCisternById$ = createEffect(() => this.actions$.pipe(
      ofType(CisternActions.public.deleteCisternById),
      switchMap((props) => this.cisternStateService.deleteCistern(props.id).pipe(
        map(() => CisternActions.private.deleteCisternSuccess({id: props.id})),
        catchError((error) => of(CisternActions.private.deleteCisternFailure({id: props.id, error}))),
      )),
    )
  );

  public refreshAfterMutation$ = createEffect(() => this.actions$.pipe(
      ofType(
        CisternActions.private.createCisternSuccess,
        CisternActions.private.updateCisternSuccess,
        CisternActions.private.deleteCisternSuccess,
      ),
      map(() => CisternActions.private.loadCisternsWithFilters()),
    )
  );
}
